import { createIdempotencyKey } from "@lafayette/api-client";
import type {
  StaffItineraryActivateBody,
  StaffItineraryArchiveBody,
  StaffItineraryCreateBody,
  StaffItineraryEditBody,
  StaffItineraryOperationsBody,
  StaffItineraryOutcome,
  StaffItineraryValidateBody,
  StaffItineraryValidation,
} from "@lafayette/schemas";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import { describeItineraryError, type ItineraryFailure } from "./errors";
import { itineraryKeys } from "./queries";
import { useItineraryRuntime } from "./runtime";

// Runs a staff itinerary command (MD-I9 to MD-I12) and keeps the page current. The rules match the memory commands:
//   1. Idempotency. One dialog INTENT has one `Idempotency-Key` for each body it submits. The intent starts when a dialog opens and
//      ends when it closes (`dropIntent`). After a failure where the command may have run (no answer, a 5xx, a 429) the next
//      identical attempt of the SAME dialog reuses the key, so the server runs it once and replays the stored answer. A changed
//      input gets a new key. A new dialog is a new intent: it never reuses a key, even for the same trip and the same title.
//   2. Version tokens. The page never guesses a token: `baseItineraryId` and `expectedRevision` come from the plan it read for an
//      edit of the plan of record, `expectedVersion` from the draft it read for an in-place edit, an activation or an archive.
//   3. Reload. After a success, or after a refusal that says the plan moved, every itinerary read is marked stale, so the timeline,
//      the revision list, the issues and the linked request states all read again.
// Nothing here logs. A body holds titles, notes and a reason, and none of them goes anywhere but the request.

export type ItineraryCommand =
  | Readonly<{ command: "createPlan"; body: StaffItineraryCreateBody }>
  | Readonly<{ command: "editDraft"; itineraryId: string; body: StaffItineraryOperationsBody }>
  | Readonly<{ command: "edit"; tripId: string; body: StaffItineraryEditBody }>
  | Readonly<{ command: "activate"; itineraryId: string; body: StaffItineraryActivateBody }>
  | Readonly<{ command: "archive"; itineraryId: string; body: StaffItineraryArchiveBody }>;

export type ItineraryCommandResult =
  | Readonly<{ ok: true; outcome: StaffItineraryOutcome }>
  | Readonly<{ ok: false; failure: ItineraryFailure }>;

const signatureOf = (request: ItineraryCommand): string =>
  JSON.stringify([
    request.command,
    "itineraryId" in request ? request.itineraryId : "tripId" in request ? request.tripId : "",
    request.body,
  ]);

export function useItineraryCommands() {
  const { api } = useItineraryRuntime();
  const queryClient = useQueryClient();
  // Keys by intent and body. An intent ends with its dialog.
  const keys = useRef(new Map<string, string>());
  const mayHaveRun = useRef(false);
  const [pendingCount, setPendingCount] = useState(0);

  const send = useCallback(
    (request: ItineraryCommand, idempotencyKey: string): Promise<StaffItineraryOutcome> => {
      const options = { idempotencyKey };
      switch (request.command) {
        case "createPlan":
          return api.createPlan(request.body, options);
        case "editDraft":
          return api.editDraft(request.itineraryId, request.body, options);
        case "edit":
          return api.edit(request.tripId, request.body, options);
        case "activate":
          return api.activate(request.itineraryId, request.body, options);
        case "archive":
          return api.archive(request.itineraryId, request.body, options);
      }
    },
    [api],
  );

  const reloadAll = useCallback(
    () => queryClient.invalidateQueries({ queryKey: itineraryKeys.all }),
    [queryClient],
  );

  const run = useCallback(
    async (request: ItineraryCommand, intent: string): Promise<ItineraryCommandResult> => {
      const signature = `${intent}\u0000${signatureOf(request)}`;
      const key = keys.current.get(signature) ?? createIdempotencyKey();
      keys.current.set(signature, key);

      setPendingCount((count) => count + 1);
      try {
        const outcome = await send(request, key);
        keys.current.delete(signature);
        mayHaveRun.current = false;
        void reloadAll();
        return { ok: true, outcome };
      } catch (error) {
        const failure = describeItineraryError(error);
        if (!failure.retryable) keys.current.delete(signature);
        else mayHaveRun.current = true;
        if (failure.reload) void reloadAll();
        return { ok: false, failure };
      } finally {
        setPendingCount((count) => count - 1);
      }
    },
    [reloadAll, send],
  );

  /**
   * The validation writes a report row and changes no plan content: it has no idempotency key. `expectedVersion` is the version the
   * page shows, so a report about another version is refused (`itinerary.version_conflict`) instead of shown against the wrong plan.
   */
  const validate = useCallback(
    async (
      itineraryId: string,
      body: StaffItineraryValidateBody,
    ): Promise<
      { ok: true; result: StaffItineraryValidation } | { ok: false; failure: ItineraryFailure }
    > => {
      setPendingCount((count) => count + 1);
      try {
        const result = await api.validate(itineraryId, { body });
        void reloadAll();
        return { ok: true, result };
      } catch (error) {
        const failure = describeItineraryError(error);
        if (failure.reload) void reloadAll();
        return { ok: false, failure };
      } finally {
        setPendingCount((count) => count - 1);
      }
    },
    [api, reloadAll],
  );

  /** The dialog of an intent is gone: its keys are dropped, so nothing it sent can be replayed by another dialog. */
  const dropIntent = useCallback((intent: string) => {
    for (const signature of [...keys.current.keys()]) {
      if (signature.startsWith(`${intent}\u0000`)) keys.current.delete(signature);
    }
  }, []);

  /** Call when a dialog closes: after a failure that may have run, the plan is read again. */
  const reloadAfterClose = useCallback(() => {
    if (!mayHaveRun.current) return;
    mayHaveRun.current = false;
    void reloadAll();
  }, [reloadAll]);

  return { run, validate, dropIntent, reloadAfterClose, pending: pendingCount > 0 };
}
